import { isServer } from '@helpers/config';

import { CUSTOM_EVENTS } from '@constants/provider';

import { sendTracking } from './tracking';

export type FaiComparatorSelectedOffer = {
    rangeNg: string;
    commercialSeries: string;
};

export type FaiComparatorSelectionEventDetail = {
    selectedOffers: FaiComparatorSelectedOffer[];
};

export const FAI_COMPARATOR_DESKTOP_LIMIT = 3;
export const FAI_COMPARATOR_MOBILE_LIMIT = 2;

export const FAI_COMPARATOR_SELECTION_BREAKPOINT = '(max-width: 767px)';

const getMaxFaiComparatorSelection = () => {
    if (isServer()) {
        return FAI_COMPARATOR_DESKTOP_LIMIT;
    }

    return window.matchMedia(FAI_COMPARATOR_SELECTION_BREAKPOINT).matches
        ? FAI_COMPARATOR_MOBILE_LIMIT
        : FAI_COMPARATOR_DESKTOP_LIMIT;
};

const getOfferKey = (offer: FaiComparatorSelectedOffer) => {
    if (!offer.rangeNg || !offer.commercialSeries) {
        return undefined;
    }

    return `${offer.rangeNg}-${offer.commercialSeries}`.toLowerCase();
};

const sanitizeSelection = (selection: FaiComparatorSelectedOffer[]): FaiComparatorSelectedOffer[] => {
    const sanitized: FaiComparatorSelectedOffer[] = [];

    selection.forEach((offer) => {
        const key = getOfferKey(offer);

        if (sanitized.some((item) => getOfferKey(item) === key)) {
            return;
        }

        sanitized.push({
            rangeNg: offer.rangeNg,
            commercialSeries: offer.commercialSeries,
        });
    });

    return sanitized.slice(0, getMaxFaiComparatorSelection());
};

let activeSelection: FaiComparatorSelectedOffer[] = [];

const emitSelectionChange = (selection: FaiComparatorSelectedOffer[]) => {
    if (isServer()) {
        return;
    }

    const detail: FaiComparatorSelectionEventDetail = {
        selectedOffers: selection,
    };

    const event = new CustomEvent<FaiComparatorSelectionEventDetail>(CUSTOM_EVENTS.FAI_COMPARATOR_SELECTION, {
        detail,
    });

    window.dispatchEvent(event);
};

export function getStoredFaiComparatorSelection(): FaiComparatorSelectedOffer[] {
    return [...activeSelection];
}

export function setFaiComparatorSelection(
    selection: FaiComparatorSelectedOffer[],
    options: { emit?: boolean } = { emit: true },
) {
    const sanitizedSelection = sanitizeSelection(selection);
    activeSelection = sanitizedSelection;

    if (options.emit !== false) {
        emitSelectionChange(sanitizedSelection);
    }

    return sanitizedSelection;
}

export function updateFaiComparatorSelection(
    offer: FaiComparatorSelectedOffer,
    shouldSelect: boolean,
): FaiComparatorSelectedOffer[] {
    const currentSelection = getStoredFaiComparatorSelection();
    const offerKey = getOfferKey(offer);

    if (!offerKey) {
        return currentSelection;
    }

    const selectionWithoutOffer = currentSelection.filter((item) => getOfferKey(item) !== offerKey);

    if (shouldSelect) {
        if (selectionWithoutOffer.length >= getMaxFaiComparatorSelection()) {
            activeSelection = selectionWithoutOffer;
            emitSelectionChange(activeSelection);
            return activeSelection;
        }

        return setFaiComparatorSelection([...selectionWithoutOffer, offer]);
    }

    return setFaiComparatorSelection(selectionWithoutOffer);
}

export function sendComparatorButtonTracking(selectedOffers: FaiComparatorSelectedOffer[]) {
    const offerNames = selectedOffers.map((offer) => `${offer.rangeNg}-${offer.commercialSeries}`).join('_');

    sendTracking(false, {
        action: 'Clic_redirection',
        category: 'Comparateur_box',
        name: 'Voir_comparateur_' + offerNames,
    });
}
